import { LuCirclePlus, LuCloudDownload, LuInfo, LuUpload } from 'react-icons/lu'

import { FeatureFlag } from '@/feature/feature-flags'
import { GoogleLoader } from '@/feature/map'

import { Button } from '@/lib/core/components/Button'
import { PageTop } from '@/lib/core/components/Layout'
import { Pagination } from '@/lib/core/components/Pagination'
import { Table, TableColumns } from '@/lib/core/components/Table'
import { Tooltip } from '@/lib/core/components/Tooltip'

import { CreateTaskModal } from '../CreateTaskModal'
import { DeleteTaskModal } from '../DeleteTaskModal/DeleteTaskModal'
import { ExecutionBoardFilters } from '../ExecutionBoardFilters/ExecutionBoardFilters'
import { ImportTasksModal } from '../ImportTasksModal'
import { TaskFocusModal } from '../TaskFocusModal'
import styles from './ExecutionBoard.module.css'
import { PROGRESS_TOOLTIP_TEXT } from './ExecutionBoard.utils'
import { TaskRow } from './TaskRow'
import { useExecutionBoard } from './useExecutionBoard'

export enum ExecutionBoardTabs {
  ALL = 'all',
}

const COLUMNS: TableColumns = [
  {
    accessor: 'reference',
    label: 'Reference',
    sortable: true,
    sortKey: 'task__reference',
    width: '100px',
    resizableColumn: true,
  },
  {
    accessor: 'issuer',
    label: 'Issued By',
    sortable: true,
    width: '120px',
    sortKey: 'task__issuer__name',
  },
  {
    accessor: 'description',
    label: 'Description',
    sortable: true,
    width: '80px',
    sortKey: 'task__description',
    resizableColumn: true,
  },
  {
    accessor: 'fromDatetime',
    label: 'Start',
    sortable: true,
    width: '260px',
    sortKey: 'task__from_datetime',
    resizableColumn: true,
  },
  {
    accessor: 'toDatetime',
    label: 'End',
    sortable: true,
    width: '260px',
    sortKey: 'task__to_datetime',
    resizableColumn: true,
  },
  {
    accessor: 'stops',
    label: 'Stops',
    sortable: false,
    width: '50px',
  },
  {
    accessor: 'assignedTo',
    label: 'Assigned Company',
    sortable: true,
    width: '120px',
    sortKey: 'assigned_org__name',
  },
  {
    accessor: 'assignedVehicle',
    label: 'Assigned Vehicle',
    sortable: true,
    width: '120px',
    sortKey: 'assigned_asset__vehicle__plate_number',
  },
  {
    accessor: 'status',
    label: 'Status',
    sortable: true,
    width: '160px',
  },
  {
    accessor: 'progress',
    label: 'Progress',
    sortable: false,
    width: '120px',
    tooltip: (
      <Tooltip maxWidth={280} placement="top" tooltipContent={PROGRESS_TOOLTIP_TEXT}>
        <span
          className={styles.progressInfo}
          data-testid="progress-info-tooltip"
          aria-label="Progress column explanation"
        >
          <LuInfo size={14} color="#15A1CE" />
        </span>
      </Tooltip>
    ),
  },
  {
    accessor: 'actions',
    label: 'Actions',
    width: '100px',
  },
] as const

interface ExecutionBoardProps {
  table: ExecutionBoardTabs
}

export const ExecutionBoard = (_props: ExecutionBoardProps) => {
  const {
    data,
    isLoading,
    filters,
    tripProgress,
    refreshTasks,
    createTaskModalOpen,
    importTaskModalOpen,
    openDeleteModal,
    selectedToDelete,
    focusedTask,
    handleViewTask,
    closeFocusModal,
    handleCreateTask,
    handleImportTasks,
    handleExportTasks,
    onPaginationChange,
    onChangeFilter,
    onSearch,
    clearSearch,
    onSort,
    onDelete,
    closeDeleteModal,
    onTaskDeleted,
    closeCreateModal,
    closeImportModal,
  } = useExecutionBoard()

  return (
    <FeatureFlag flag="featureExecutionBoardUi">
      <GoogleLoader>
        <div className={styles.executionBoard}>
          <PageTop title={'Execution Board'}>
            <div className={styles.executionBoardActions}>
              <Button
                leftIcon={<LuCloudDownload size="24" />}
                onClick={handleImportTasks}
                size="large"
                variant="ghost"
              >
                Import
              </Button>
              <Button
                onClick={handleExportTasks}
                size="large"
                variant="ghost"
                leftIcon={<LuUpload size="24" />}
              >
                Export
              </Button>
              <Button
                leftIcon={<LuCirclePlus size="24" />}
                onClick={handleCreateTask}
                size="large"
                variant="tertiary"
              >
                Create
              </Button>
            </div>
          </PageTop>
          <div className={styles.container}>
            <Table
              resizableTable={true}
              columns={COLUMNS}
              isLoading={isLoading}
              isSelectable={false}
              elementsCounter={data.count}
              onSort={onSort}
              sortInfo={filters.ordering}
              onSearch={onSearch}
              clearSearch={clearSearch}
              renderCustomFilter={
                <ExecutionBoardFilters
                  onChangeFilter={onChangeFilter}
                  tasks={data.results}
                  filters={filters}
                />
              }
              dataTestId="execution-board-table"
            >
              {data.results?.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  tripProgress={tripProgress[task.taskDefinition.id]}
                  onRefresh={refreshTasks}
                  onViewTask={handleViewTask}
                  onDelete={onDelete}
                />
              ))}
            </Table>
            <Pagination
              initialPage={filters.page}
              initialPageSize={filters.pageSize}
              isLoading={isLoading}
              onChange={onPaginationChange}
              recordsCount={data.count}
            />
          </div>
          <CreateTaskModal
            isActive={createTaskModalOpen}
            onClose={closeCreateModal}
            onTaskCreated={refreshTasks}
          />
          <ImportTasksModal
            isActive={importTaskModalOpen}
            onClose={closeImportModal}
            onImported={refreshTasks}
          />
          {selectedToDelete && (
            <DeleteTaskModal
              closeModal={closeDeleteModal}
              isOpen={openDeleteModal}
              task={selectedToDelete}
              onTaskDeleted={onTaskDeleted}
            />
          )}
          {/* Mounted only while open so the map is never built into a closed, zero-size dialog. */}
          {focusedTask && (
            <TaskFocusModal
              task={focusedTask}
              tripProgress={tripProgress[focusedTask.taskDefinition.id]}
              onClose={closeFocusModal}
            />
          )}
        </div>
      </GoogleLoader>
    </FeatureFlag>
  )
}
